/**
 * NightMask — darkness the fire has to push back.
 *
 * Night was applied as a flat brightness multiply across the whole frame, so
 * the grove went uniformly dim and the fire sat on top of it as a bright blob
 * that lit nothing. Everything was equally dark whether you stood at the hearth
 * or thirty feet into the trees, which is the opposite of the premise: the fire
 * is supposed to be the light you see by.
 *
 * This is a full-viewport dark plate with a radial hole erased at the fire. The
 * grove is dark, the clearing around the hearth is not, and the edge between
 * them moves with the fuel — so feeding the fire visibly opens the scene up and
 * letting it fall closes the walls in. In Phase 2 that same radius becomes the
 * player's reach.
 *
 * The plate lives on the main camera, so the unfiltered light camera that draws
 * the flame is unaffected and the fire itself never gets darkened by it.
 *
 * Drawn with a RenderTexture rather than a shader: one fill and one erase per
 * frame, no pipeline to maintain, and it composites correctly against the
 * colour grader already on the camera.
 */
import * as Phaser from 'phaser';

/**
 * One light, in SCREEN space.
 *
 * The mask started with the fire hard-coded as the only light in the world,
 * which was true right up until something else in the grove could be lit. It
 * takes a list now — the hearth, the lantern wherever it has been set down,
 * and whatever comes after.
 */
export interface MaskLight {
  x: number;
  y: number;
  /** How far it throws light, in px. */
  radius: number;
  /** 0..1 — how strong the warm add-back is. A dying fire still lifts a face. */
  warmth: number;
  /** 0..1 — how much this light gutters. A lamp is steadier than a bonfire. */
  flicker: number;
}

const HOLE_TEXTURE = '__night_mask_hole__';
const WARM_TEXTURE = '__night_mask_warm__';
const HOLE_SIZE = 512;

/**
 * Deep blue rather than blue-black. At 0x080d16 and 0.88 opacity, 16 of 58
 * sampled background points crushed below luminance 5 — flat black with no
 * recoverable detail, which throws away the painted mid and far ground the
 * scene is supposed to be admired for. Night should desaturate the forest, not
 * delete it.
 */
const NIGHT_COLOR = 0x121a2b;

/**
 * How dark it gets at full night. Short of 1 on purpose: a silhouette you can
 * just make out is atmospheric, a black rectangle is a bug report.
 */
const MAX_DARKNESS = 0.78;

/** The lit area is this much wider than the fire's own light radius. */
const HOLE_SCALE = 2.4;

/**
 * Additive firelight over the pool.
 *
 * A mask can only ever SUBTRACT: erasing a hole reveals the scene underneath,
 * and that scene has already been darkened by the colour grader, so the lit
 * pool could never come out brighter than the dark it sits in. Measured, the
 * character's shadow side at low fuel was darker than its own background — a
 * rim-lit sliver rather than a legible figure. Light has to be ADDED.
 *
 * The floor matters as much as the range: this must not fade out with the fire,
 * because a dying fire is exactly when the player most needs to read the scene.
 */
const LIGHT_ALPHA_MIN = 0.20;
const LIGHT_ALPHA_MAX = 0.34;
const LIGHT_SCALE = 1.5;

/** Flicker on the lit radius, so the treeline breathes with the flame. */
const FLICKER_HZ = 5.5;
const FLICKER_DEPTH = 0.055;

export class NightMask {
  private readonly scene: Phaser.Scene;
  private readonly rt: Phaser.GameObjects.RenderTexture;
  private readonly hole: Phaser.GameObjects.Image;
  private readonly warms: Phaser.GameObjects.Image[] = [];
  private depth: number;
  private t = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, depth: number) {
    this.scene = scene;
    NightMask.ensureHoleTexture(scene);
    NightMask.ensureWarmTexture(scene);

    const w = scene.scale.width;
    const h = scene.scale.height;
    this.rt = scene.add.renderTexture(0, 0, w, h);
    this.rt.setOrigin(0, 0);
    // Pinned to the viewport: this is a screen-space effect, so it must not
    // travel with the world the way the painted planes do.
    this.rt.setScrollFactor(0);
    this.rt.setDepth(depth);

    // Off-list stamp used only as an erase brush, never added to the scene.
    this.hole = new Phaser.GameObjects.Image(scene, 0, 0, HOLE_TEXTURE);
    this.hole.setOrigin(0.5, 0.5);
    this.depth = depth;
    // Additive light lives ABOVE the plate rather than in it — compositing it
    // into the RenderTexture would only tint, never brighten. One sprite per
    // light, made on demand.
  }

  /**
   * @param darkness 0..1 — how much night is in effect.
   * @param fireX    fire position in SCREEN space.
   * @param fireY    fire position in SCREEN space.
   * @param radiusPx how far the fire throws light, in px.
   * @param delta    frame delta in ms.
   */
  update(darkness: number, lights: ReadonlyArray<MaskLight>, delta: number): void {
    if (this.destroyed) return;
    this.t += delta / 1000;

    const amount = Phaser.Math.Clamp(darkness, 0, 1) * MAX_DARKNESS;
    const visible = amount > 0.01;
    this.rt.setVisible(visible);
    if (!visible) {
      for (const w of this.warms) w.setVisible(false);
      return;
    }

    this.rt.clear();
    this.rt.fill(NIGHT_COLOR, amount);

    // One shared guttering wave, so every light in the grove breathes on the
    // same air rather than each keeping its own private time.
    const gutter = Math.sin(this.t * Math.PI * 2 * FLICKER_HZ)
      + Math.sin(this.t * Math.PI * 2 * FLICKER_HZ * 0.37) * 0.6;

    for (let i = 0; i < lights.length; i++) {
      const light = lights[i];
      const flicker = 1 + gutter * FLICKER_DEPTH * Phaser.Math.Clamp(light.flicker, 0, 1);
      // A fire with no fuel still leaves embers; the hole never closes fully or
      // the screen becomes an unreadable black rectangle.
      const diameter = light.radius * HOLE_SCALE * flicker;
      this.hole.setScale(diameter / HOLE_SIZE);
      this.rt.erase(this.hole, light.x, light.y);

      // Then add light back. Scaled by darkness so noon is untouched, but with
      // a floor in warmth so the pool keeps lifting the character as the fire
      // dies.
      const warm = this.warmAt(i);
      const lightAlpha = Phaser.Math.Linear(
        LIGHT_ALPHA_MIN, LIGHT_ALPHA_MAX, Phaser.Math.Clamp(light.warmth, 0, 1),
      );
      warm.setVisible(true);
      warm.setScale((diameter * LIGHT_SCALE) / HOLE_SIZE);
      warm.setAlpha(lightAlpha * amount * flicker);
      warm.setPosition(light.x, light.y);
    }

    // Lights can go out — a lantern picked up is one fewer pool on the ground —
    // so anything left over from a longer frame has to be hidden, not left
    // burning where nothing is.
    for (let i = lights.length; i < this.warms.length; i++) {
      this.warms[i].setVisible(false);
    }
  }

  /** Warm sprites are pooled: made once per light, reused every frame after. */
  private warmAt(i: number): Phaser.GameObjects.Image {
    let warm = this.warms[i];
    if (warm) return warm;
    warm = this.scene.add.image(0, 0, WARM_TEXTURE);
    warm.setOrigin(0.5, 0.5);
    warm.setBlendMode(Phaser.BlendModes.ADD);
    warm.setScrollFactor(0);
    warm.setDepth(this.depth + 1);
    this.warms.push(warm);
    return warm;
  }

  setDepth(d: number): void {
    this.depth = d;
    this.rt.setDepth(d);
    for (const w of this.warms) w.setDepth(d + 1);
  }

  /** The plate is viewport-sized; re-make it when the viewport changes. */
  resize(): void {
    if (this.destroyed) return;
    this.rt.setSize(this.scene.scale.width, this.scene.scale.height);
  }

  getGameObject(): Phaser.GameObjects.RenderTexture {
    return this.rt;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.rt.destroy();
    this.hole.destroy();
    for (const w of this.warms) w.destroy();
    this.warms.length = 0;
  }

  /**
   * Soft-edged radial brush. The falloff matters more than the radius: a hard
   * circle reads as a spotlight cut into a curtain, and the whole point is that
   * firelight has no edge, it just runs out.
   */
  private static ensureHoleTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(HOLE_TEXTURE)) return;
    const canvas = document.createElement('canvas');
    canvas.width = HOLE_SIZE;
    canvas.height = HOLE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const r = HOLE_SIZE / 2;
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    // Hold near-full erase well out from the centre, then fall away quickly.
    // The first curve started dropping at 32% of the radius, which ate into the
    // character sitting at the hearth and left her a silhouette exactly when
    // the player most needs to read her.
    g.addColorStop(0.00, 'rgba(255,255,255,1)');
    g.addColorStop(0.46, 'rgba(255,255,255,0.99)');
    g.addColorStop(0.66, 'rgba(255,255,255,0.78)');
    g.addColorStop(0.84, 'rgba(255,255,255,0.30)');
    g.addColorStop(1.00, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, HOLE_SIZE, HOLE_SIZE);
    scene.textures.addCanvas(HOLE_TEXTURE, canvas);
  }

  /** Amber wash laid back over the lit pool. */
  private static ensureWarmTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(WARM_TEXTURE)) return;
    const canvas = document.createElement('canvas');
    canvas.width = HOLE_SIZE;
    canvas.height = HOLE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const r = HOLE_SIZE / 2;
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    g.addColorStop(0.00, 'rgba(255, 176, 88, 0.85)');
    g.addColorStop(0.40, 'rgba(255, 142, 56, 0.52)');
    g.addColorStop(0.72, 'rgba(206, 96, 34, 0.20)');
    g.addColorStop(1.00, 'rgba(150, 62, 20, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, HOLE_SIZE, HOLE_SIZE);
    scene.textures.addCanvas(WARM_TEXTURE, canvas);
  }
}
